'use client';

import clsx from 'clsx';
import { useRef, useEffect, Fragment } from 'react';
import styles from './Dropdown.module.scss';
import type { DropdownOption } from './Dropdown';
import type { FieldSize } from '../formgroup/FormGroup';
import Icon from '@publishing/components/ui/Icon';
import { ActionCheckType2, ActionCloseType1 } from '@publishing/components/ui/SvgSprite';

interface OptionListProps {
  listId?: string;
  triggerId?: string;
  size: FieldSize;
  options: DropdownOption[];
  value: string;
  onSelect: (option: DropdownOption) => void;
  onClose?: () => void;
  withScrollbar?: boolean;
  // 바텀시트 전환 여부: Dropdown에서 isSheet prop을 받아 내려옴
  isSheet?: boolean;
  sheetHeaderType?: 'basic' | 'custom';
  // 현재 모바일 여부: isSheet가 true일 때 클래스/헤더 활성화 조건
  isMobile?: boolean;
  label?: string;
}

export default function OptionList({
  listId,
  triggerId,
  options,
  value,
  onSelect,
  onClose,
  withScrollbar = false,
  isSheet = false,
  sheetHeaderType = 'basic',
  isMobile = false,
  label,
}: OptionListProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // isSheet + isMobile 둘 다 true일 때만 시트 모드 활성화
  const isSheetMode = isSheet && isMobile;

  const sheetHeaderClass = {
    basic: styles.sheet_header_basic,
    custom: styles.sheet_header_custom,
  }[sheetHeaderType];

  const sheetLabelClass = {
    basic: styles.sheet_label_basic,
    custom: styles.sheet_label_custom,
  }[sheetHeaderType];

  const updateThumb = () => {
    const el = listRef.current;
    const thumb = thumbRef.current;
    const track = trackRef.current;
    if (!el || !thumb || !track) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const ratio = clientHeight / scrollHeight;

    const INSET = 20;
    const trackH = clientHeight - INSET * 2;
    track.style.height = `${trackH}px`;

    if (ratio >= 1) {
      track.style.opacity = '0';
      track.dataset.visible = 'false';
      return;
    }

    const thumbH = Math.max(ratio * trackH, 32);
    const maxThumbTop = trackH - thumbH;
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;

    track.style.opacity = '1';
    track.dataset.visible = 'true';
    thumb.style.height = `${thumbH}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  };

  useEffect(() => {
    // 시트 모드에서는 네이티브 스크롤 사용 → 커스텀 스크롤바 불필요
    if (!withScrollbar || isSheetMode) return;
    const el = listRef.current;
    const wrap = el?.parentElement;
    if (!el || !wrap) return;

    const rafId = requestAnimationFrame(updateThumb);
    el.addEventListener('scroll', updateThumb);

    const ro = new ResizeObserver(updateThumb);
    ro.observe(wrap);

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', updateThumb);
      ro.disconnect();
    };
     
  }, [withScrollbar, isSheetMode]);

  const panelCls = clsx(styles.option_list_panel, isSheetMode && styles.option_list_panel_sheet);

  return (
    <>
      {/* 딤: 시트 모드에서만 렌더 */}
      {isSheetMode && (
        <div className={styles.dim} onClick={onClose} aria-hidden="true" />
      )}

      <div
        className={panelCls}
        role={isSheetMode ? 'dialog' : undefined}
        aria-modal={isSheetMode ? true : undefined}
        aria-label={isSheetMode ? label : undefined}
      >
        {/* 헤더(라벨 + X버튼): 시트 모드에서만 렌더 */}
        {isSheetMode && (
          <div className={[styles.sheet_header, sheetHeaderClass].filter(Boolean).join(' ')}>
            {label && (
              <span className={sheetLabelClass}>{label}</span>
            )}
            <button
              type="button"
              className={styles.sheet_close}
              onClick={onClose}
              aria-label="닫기"
            >
              <Icon name={ActionCloseType1} size={24} aria-hidden />
            </button>
          </div>
        )}

        <ul
          ref={listRef}
          id={listId}
          className={styles.option_list}
          role="listbox"
          aria-labelledby={triggerId}
        >
          {options.map((option, index) => {
            const prev = options[index - 1];
            const showGroupHeader =
              Boolean(option.groupTitle?.trim()) &&
              (index === 0 || prev.groupTitle !== option.groupTitle);

            return (
              <Fragment key={option.value}>
                {showGroupHeader && (
                  <li className={styles.option_group_header} role="presentation">
                    {option.groupTitle}
                  </li>
                )}
                <li
                  className={clsx(styles.option, option.value === value && styles.option_selected)}
                  role="option"
                  aria-selected={option.value === value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(option);
                  }}
                >
                  <span>{option.label}</span>

                  {option.value === value && (
                    <Icon name={ActionCheckType2} size={24} />
                  )}
                </li>
              </Fragment>
            );
          })}
        </ul>

        {/* 커스텀 스크롤바: 데스크탑 드롭다운에서만 */}
        {withScrollbar && !isSheetMode && (
          <div ref={trackRef} className={styles.scrollbar_track} style={{ opacity: 0 }}>
            <div ref={thumbRef} className={styles.scrollbar_thumb} />
          </div>
        )}
      </div>
    </>
  );
}
